import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../mock';

export const Services = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Driving Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive driving instruction services designed to help you become a confident, 
            safe driver. All lessons are conducted by a fully qualified DVSA instructor.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={service.id} className={`hover:shadow-xl transition-all duration-300 ${index === 0 ? 'border-red-500 border-2' : ''}`}>
                <CardHeader>
                  {index === 0 && (
                    <div className="bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full w-fit mb-2">
                      MOST POPULAR
                    </div>
                  )}
                  <CardTitle className="text-2xl text-gray-900">{service.name}</CardTitle>
                  {service.price && (
                    <div className="flex items-baseline space-x-2">
                      <span className="text-3xl font-bold text-red-600">{service.price}</span>
                      <span className="text-gray-500">{service.duration}</span>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">What's included:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Link to="/contact">
                    <Button className={`w-full ${index === 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-900 hover:bg-gray-800'} text-white py-3`}>
                      {service.id === 2 || service.id === 3 ? 'Send Enquiry' : 'Book This Service'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Our Services?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Every service is delivered with the highest standards of professionalism and safety
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Flexible Scheduling</h3>
              <p className="text-gray-600">
                Book lessons at times that suit your schedule, including evenings and weekends.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Patient Instruction</h3>
              <p className="text-gray-600">
                Learn at your own pace in a supportive, stress-free environment designed for success.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Proven Results</h3>
              <p className="text-gray-600">
                High first-time pass rate with comprehensive test preparation and local area knowledge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Coverage Areas</h2>
            <p className="text-gray-600">
              Professional driving instruction across the Inverclyde region 
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <h3 className="font-semibold text-lg text-red-600 mb-2">Wemyss Bay</h3>
                <p className="text-gray-600 text-sm">Full coverage area</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-red-600 mb-2">Greenock</h3>
                <p className="text-gray-600 text-sm">Full coverage area</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-red-600 mb-2">Gourock</h3>
                <p className="text-gray-600 text-sm">Full coverage area</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-red-600 mb-2">Surrounding Areas</h3>
                <p className="text-gray-600 text-sm">Contact for availability</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-red-100">
            Choose the service that's right for you and take the first step towards driving independence.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold">
              Book Your Lesson Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;